import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import ModifierToken from './ModifierToken';

/**
 * TraitSlot - A compact token slot for a trait
 *
 * Displays the trait name above and a slot below that can:
 * - Accept dropped tokens (drop zone)
 * - Display assigned token (draggable to move it)
 * - Show empty state when no token assigned
 */
const TraitSlot = ({ traitName, value }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: traitName,
    data: { traitName }
  });

  const isEmpty = value === undefined || value === null;

  return (
    <div className="flex flex-col items-center gap-2">
      {/* Trait name label */}
      <div className="text-center">
        <span className="text-xs font-semibold text-gray-300 uppercase tracking-wide">
          {traitName}
        </span>
      </div>

      {/* Token slot (drop zone) */}
      <div
        ref={setNodeRef}
        className={`
          relative flex items-center justify-center
          w-20 h-20 lg:w-16 lg:h-16
          rounded-lg border-2
          transition-all duration-200
          touch-none
          ${isEmpty
            ? 'border-dashed border-gray-600 bg-gray-800/30'
            : 'border-gray-500 bg-gray-800/50'
          }
          ${isOver ? 'border-yellow-400 bg-yellow-900/30 scale-105' : ''}
        `}
        role="region"
        aria-label={`${traitName} trait slot${isEmpty ? ', empty' : `, contains ${value > 0 ? '+' : ''}${value}`}`}
        aria-live="polite"
      >
        {isEmpty ? (
          // Empty slot indicator
          <span className="text-gray-600 text-2xl">∅</span>
        ) : (
          // Token in slot (draggable to move it)
          <ModifierToken
            id={`slot-${traitName}-${value}`}
            value={value}
            sourceSlot={traitName}
          />
        )}

        {/* Drag-over highlight */}
        {isOver && (
          <div className="absolute inset-0 rounded-lg bg-yellow-400/20 pointer-events-none animate-pulse" />
        )}
      </div>
    </div>
  );
};

export default TraitSlot;
